import type { FC } from 'react';
import React, { Fragment } from 'react';
import BEMHelper from '@ynap/bem-helper';
import { ValidatedField } from '@ynap/field';
import Flag from '@ynap/flag';
import type { FieldRenderProps } from '@ynap/form';
import { Field as FormField } from '@ynap/form';
import { CombinedSelect } from '@ynap/select';
import { getSearchedCountries, validate } from './utils';
import type { Field, Messages } from './@types';

const afBem = new BEMHelper('AddressForm');

interface CountrySelectFieldProps {
    innerKey: string;
    className: string;
    field: Field;
    messages?: Messages;
    help?: string;
    errorStyle?: string;
    initialValue?: string;
}

export const CountrySelectField: FC<CountrySelectFieldProps> = ({ className, errorStyle = 'error', field, help, initialValue, innerKey, messages }) => {
    const { values, wcsType } = field;
    const name = (wcsType?.name || 'country').toString();
    const displayName =
        messages && messages.addressForm && messages.addressForm.display && messages.addressForm.display.country
            ? messages.addressForm.display.country()
            : 'Country';
    const selectLabel =
        messages && messages.addressForm && messages.addressForm.select ? messages.addressForm.select({ select: displayName }) : 'Select country';

    const options = values
        ? values
              // eslint-disable-next-line no-nested-ternary
              .sort((a, b) => (typeof a !== 'string' && typeof b !== 'string' ? (a.display < b.display ? -1 : 1) : a < b ? -1 : 1))
              .map((option) => {
                  const display = typeof option !== 'string' ? option.display : option;
                  const value = typeof option !== 'string' ? option.value : option;
                  return {
                      key: value,
                      value,
                      label: (
                          <Fragment>
                              <Flag countryISO={value.toUpperCase()} />
                              <span className={afBem('flagLabel')}>{display}</span>
                          </Fragment>
                      ),
                      labelNative: display,
                  };
              })
        : [];

    return (
        <FormField key={innerKey} name={name} validate={validate(messages, field, displayName)} value={initialValue || ''}>
            {({ errors, onBlur, onChange, touched, value }: FieldRenderProps) => {
                const error = touched && errors && errors[0] ? errors[0] : undefined;
                return (
                    <ValidatedField className={className} error={error} errorStyle={errorStyle} help={help} htmlFor={name} label={displayName}>
                        <div>
                            <CombinedSelect
                                key={name}
                                enableSearch
                                ariaLabel={displayName}
                                emptyLabel={selectLabel}
                                id={name}
                                onDropdown={() => onBlur(value)}
                                options={options}
                                searchFilter={getSearchedCountries}
                                searchPlaceholder={messages?.addressForm?.search?.()}
                                value={value}
                                onChange={(value: string) => {
                                    onChange(value);
                                    onBlur(value);
                                }}
                            />
                        </div>
                    </ValidatedField>
                );
            }}
        </FormField>
    );
};
